import { Auth } from "aws-amplify";
import { getCookie, setCookie } from "lib/helpers/loginHelpers.js";
import { removeNotification } from "lib/toastNotification.js";
import { getUserPoolProjects } from "config/getProjectList.js";
import { explorerNavigation} from "actions/navigation";
import { setCurrentDatasetId } from "actions/dataExplorerActions.js";
import { EXPLORER_PATH } from "constants/constants.js"
import { DATASET_ID_ENTITIES } from "layout/DataExplorer/DataExplorerConstants.js";
import { push } from "connected-react-router";

import {
	ATTEMPTING_LOG_IN,
	BACK_TO_LOGIN,
	CHANGE_PASSWORD_ERROR,
	CHANGE_PASSWORD_SUCCESS,
	CLOSE_CHANGE_PASSWORD_MODAL,
	CLOSE_USER_DROPDOWN,
	CREATE_ORIGINAL_PASSWORD_ERROR,
	CREATE_ORIGINAL_PASSWORD_SUCCESS,
	FORCE_LOG_OUT,
	FORCE_PASSWORD_UPDATE,
	LOG_IN_ERROR,
	LOG_IN_SUCCESS,
	NOT_LOGGED_IN,
	SET_API,
	SET_BROWSER_TYPE,
	SET_PROJECT,
	SET_PROJECT_THEME,
	SIGNED_OUT,
	RECOVER_PASSWORD_ERROR,
	RECOVER_PASSWORD_SUCCESS,
	SET_TERMS_AND_CONDITIONS_ACCEPTED,
	OPEN_CHANGE_PASSWORD_MODAL,
	OPEN_FORGOT_PASSWORD_VIEW,
	RESET_DATA,
	RESET_PASSWORD_ERROR,
	RESET_PASSWORD_SUCCESS,
	UPDATE_CHANGE_PASSWORD_NEW,
	UPDATE_CHANGE_PASSWORD_OLD,
	UPDATE_LOGIN_EMAIL,
	UPDATE_LOGIN_NEW_PASSWORD,
	UPDATA_LOGIN_VERIFICATION_CODE,
	UPDATE_PASSWORD,
} from "actions/constants.js";
import { configProject } from "./configProject";

// NOTE: This file is in .prettierignore

global.fetch = require("cross-fetch");


const finalizeLogin = (dispatch, getState, user, userJustLoggedIn) => {
	// Does this user need to change their password?
	if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
		dispatch({ type: FORCE_PASSWORD_UPDATE, user, userJustLoggedIn });
	} else {
		// Determine/Validate initial project to use
		const { currentProject } = getState().login;
		const projectCookie = getCookie("project", user.username);
		const userPool = user.signInUserSession && user.signInUserSession.idToken.payload["cognito:groups"];
		const projectCookieValid = userPool.find((projectId) => {	return projectId === projectCookie;	});
		const validUserProjects = getUserPoolProjects(user, userPool);		
		const projectToUse = (projectCookieValid && projectCookie) || currentProject || (validUserProjects[0] && validUserProjects[0].id) || "";

		removeNotification();
		dispatch(configProject(projectToUse));
		dispatch({ type: LOG_IN_SUCCESS, user, currentProject: projectToUse });
	}
}

// This runs on app load - is there a current user session?
export function checkForCurrentSession() {
	return (dispatch, getState) => {
		// Check for logged in user
		return Auth.currentAuthenticatedUser()
			.then((user) => {
				finalizeLogin(dispatch, getState, user, false);
			})
			.catch((error) => dispatch({ type: NOT_LOGGED_IN, error }));
	};
}

export function attemptLogIn(username, password) {
	return (dispatch, getState) => {
		const { termsAndConditionsAccepted } = getState().login;
		dispatch({ type: ATTEMPTING_LOG_IN });
		return Auth.signIn(username, password)
			.then((user) => {
				finalizeLogin(dispatch, getState, user, true);
			})
			.catch((error) => dispatch({ type: LOG_IN_ERROR, error }));
	};
}

export function forceLogOut() {
	return {
		type: FORCE_LOG_OUT,
	};
}

const T_AND_C_STORAGE_KEY = "t-and-c";

export function checkTermsAndConditionsAccepted() {
	return (dispatch, getState) => {
		// Pull any T&C acceptance data for this user from localstorage
		const tAndCObjectSerialized = localStorage.getItem(T_AND_C_STORAGE_KEY);
		if (tAndCObjectSerialized) {
			const tAndCObject = JSON.parse(tAndCObjectSerialized)
			const acceptedTime = tAndCObject.timestamp;
			const currentTime = new Date().getTime();
			const oneYear = 1000 * 60 * 60 * 24 * 365;
			// If the acceptance is valid then update state and return
			if ((currentTime - oneYear) < acceptedTime) {
				dispatch({type:SET_TERMS_AND_CONDITIONS_ACCEPTED, accepted:true});
				return true;
			}
			// If the acceptance has expired then remove from localstorage
			localStorage.removeItem(T_AND_C_STORAGE_KEY);
		}
		// If we get to this point then we don't have valid acceptance on T&C 
		// for the user in the current browser so update state accordingly
		dispatch({type:SET_TERMS_AND_CONDITIONS_ACCEPTED, accepted:false});
		return false;
	};
}

export function acceptTermsAndConditions() {
	return (dispatch, getState) => {
		// Set T&C accepted data in localstorage
		const tAndCObject = {accepted: true, timestamp: new Date().getTime()};
		localStorage.setItem(T_AND_C_STORAGE_KEY, JSON.stringify(tAndCObject));
		// Update state
		dispatch({type:SET_TERMS_AND_CONDITIONS_ACCEPTED, accepted:true});
	};
}

export function rejectTermsAndConditions() {
	return (dispatch, getState) => {
		// Remove T&C accepted data from localstorage (it shouldn't  
		// be there anyway but just in case...)
		localStorage.removeItem(T_AND_C_STORAGE_KEY);
		// Log out the user
		dispatch(forceLogOut());
	};
}

export function setAPI(api) {
	return {
		type: SET_API,
		payload: { api }	
	};
}

export function setProjectTheme(projectTheme) {
	return {
		type: SET_PROJECT_THEME,
		projectTheme: projectTheme || false,
	};
}

// This invalidates "currentProject", which causes the whole app to rerender & load the new project data
export function setProject(project, userId, resetRoute=true) {
	return (dispatch, getState) => {
		const { location } = getState().router;
		const { currentProject } = getState().login;
		if (project.id !== currentProject) {
			dispatch({ type: RESET_DATA });
			setCookie("project", project.id, userId);
			dispatch(configProject(project.id));
			dispatch({
				type: SET_PROJECT,
				project: project.id,
			});
			if (location?.pathname.startsWith(EXPLORER_PATH)) {
				const { currentDatasetId } = getState().dataExplorer;
				const { explorerDatasets, availableTabs } = getState().projectConfig;
				const validDataSetIds = explorerDatasets ? explorerDatasets.map(x => x.id) : [];
				const entitiesDatasetValid = validDataSetIds.includes(DATASET_ID_ENTITIES);
				let defaultDatasetId = entitiesDatasetValid ? DATASET_ID_ENTITIES : undefined;
				if (!defaultDatasetId && explorerDatasets && explorerDatasets[0]?.id) {
					defaultDatasetId = explorerDatasets[0].id;
				}
				dispatch(setCurrentDatasetId(defaultDatasetId, explorerDatasets));
				if (availableTabs.explorer && resetRoute) {
					dispatch(explorerNavigation(defaultDatasetId));
				}
			}
			if (resetRoute) {
				dispatch(push("/"));
			}
		}
	};
}

export function setBrowserType(browser) {
	return {
		type: SET_BROWSER_TYPE,
		payload: { browserType: browser },
	};
}

// --------- Controlled inputs --------------
export function updateLoginEmail(email) {
	return {
		type: UPDATE_LOGIN_EMAIL,
		payload: { loginInputEmail: email },
	};
}

export function updatePassword(password) {
	return {
		type: UPDATE_PASSWORD,
		payload: { loginInputPassword: password },
	};
}

export function updateChangePasswordNew(password) {
	return {
		type: UPDATE_CHANGE_PASSWORD_NEW,
		payload: { loginInputChangePasswordNew: password },
	};
}

export function updateChangePasswordOld(password) {
	return {
		type: UPDATE_CHANGE_PASSWORD_OLD,
		payload: { loginInputChangePasswordOld: password },
	};
}

export function updateLoginVerificationCode(code) {
	return {
		type: UPDATA_LOGIN_VERIFICATION_CODE,
		payload: { loginInputVerificationCode: code },
	};
}

export function updateLoginNewPassword(password) {
	return (dispatch) => {
		dispatch({
			type: UPDATE_LOGIN_NEW_PASSWORD,
			payload: { loginInputNewPassword: password },
		});
	};
}

export function signOut() {
	return (dispatch) => {
		dispatch({ type: CLOSE_USER_DROPDOWN });
		return Auth.signOut()
			.then(() => {
				dispatch({ type: SIGNED_OUT });
				dispatch({ type: RESET_DATA });
			})
			.catch((error) =>
				dispatch({
					type: SIGNED_OUT,
					error,
				})
			);
	};
}

// Forgot Password
export function recoverPassword(username) {
	return (dispatch) => {
		dispatch({ type: ATTEMPTING_LOG_IN });
		// docs: https://aws.github.io/aws-amplify/api/classes/authclass.html#forgotpassword
		return Auth.forgotPassword(username)
			.then((user) =>
				dispatch({
					type: RECOVER_PASSWORD_SUCCESS,
					user,
					username,
				})
			)
			.catch((error) => {
				console.warn('recoverPassword', error);
				dispatch({
					type: RECOVER_PASSWORD_ERROR,
					error,
				})
			});
	};
}

export function openForgotPasswordView() {
	return {
		type: OPEN_FORGOT_PASSWORD_VIEW,
	};
}

export function backToLogin() {
	return {
		type: BACK_TO_LOGIN,
	};
}

export function openChangePasswordModal() {
	return {
		type: OPEN_CHANGE_PASSWORD_MODAL,
	};
}

export function closeChangePasswordModal() {
	return {
		type: CLOSE_CHANGE_PASSWORD_MODAL,
	};
}

// Submits new password, then logs user in (2 API calls)
export function resetPassword(code, password) {
	return (dispatch, getState) => {
		const username = getState().login.stashUsername;
		dispatch({ type: ATTEMPTING_LOG_IN });
		// docs: https://aws.github.io/aws-amplify/api/classes/authclass.html#forgotpasswordsubmit
		return Auth.forgotPasswordSubmit(username, code, password)
			.then(() =>
				// docs: https://aws.github.io/aws-amplify/api/classes/authclass.html#signin
				Auth.signIn(username, password)
					.then(() =>
						dispatch({
							type: RESET_PASSWORD_SUCCESS,
						})
					)
					.catch((error) =>
						dispatch({
							type: RESET_PASSWORD_ERROR,
							error,
						})
					)
			)
			.catch((error) =>
				dispatch({
					type: RESET_PASSWORD_ERROR,
					error,
				})
			);
	};
}

// Changes password for an already Cognito CONFIRMED user
export function changePassword(currentPassword, newPassword) {
	return (dispatch, getState) => {
		dispatch({ type: ATTEMPTING_LOG_IN });
		const { userData } = getState().login;
		// docs: https://aws.github.io/aws-amplify/api/classes/authclass.html#changepassword
		return Auth.changePassword(userData, currentPassword, newPassword)
			.then((user) =>
				dispatch({
					type: CHANGE_PASSWORD_SUCCESS,
					user,
					userData,
				})
			)
			.catch((error) =>
				dispatch({
					type: CHANGE_PASSWORD_ERROR,
					error,
				})
			);
	};
}

// Createa a password for a FORCE_CHANGE_PASSWORD user (when they log in for the first time)
export function createOriginalPassword(currentPassword, newPassword) {
	return (dispatch, getState) => {
		dispatch({ type: ATTEMPTING_LOG_IN });
		// The original user response is a CognitoUser instance, which has the method completeNewPasswordChallenge
		const { userData } = getState().login;
		// docs: https://docs.aws.amazon.com/cognito/latest/developerguide/using-amazon-cognito-identity-user-pools-javascript-example-authenticating-admin-created-user.html
		return userData.completeNewPasswordChallenge(
			newPassword,
			{},
			{
				onSuccess: (user) => {
					dispatch({
						type: CREATE_ORIGINAL_PASSWORD_SUCCESS,
						userData,
					});
				},
				onFailure: (error) => {
					dispatch({
						type: CREATE_ORIGINAL_PASSWORD_ERROR,
						error,
					});
				},
			}
		);
	};
}


